/**
 * MaterialityAssessSlideout — the design-approved materiality assessment surface (Epic 6.2 FE).
 *
 * A risk/compliance user supplies the rubric's explicit charter facts (the 5 exposure anchors
 * 0–4 + purpose class + production-entitlement); the platform computes the tier via the
 * green `MaterialityRubric` and persists it (POST /ownership/agents/{id}/materiality). The tier
 * is ALWAYS mapped from the rubric — never hand-set — and inputs are never guessed (automated
 * factor-derivation is deferred per decision D2 until risk-function sign-off). Live preview uses
 * the pure `/materiality/preview` endpoint so the user sees the tier before committing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/api';
import { Slideout, Button, Select, Label, Pill } from '../../components/v2';
import { Gauge, ShieldCheck } from 'lucide-react';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

// The 5 exposure factors — labels/questions are the doc-locked MaterialityInputs contract (v1.0 §2).
const FACTORS = [
  { key: 'e1DecisionSignificance', label: 'E1 · Decision significance', hint: 'Do outputs drive business / risk decisions?' },
  { key: 'e2ActionReach',          label: 'E2 · Action reach',          hint: 'What can it change in the world? (E2=4 → floors at Tier 1)' },
  { key: 'e3DataSensitivity',      label: 'E3 · Data sensitivity',      hint: 'Most sensitive data class touched (E3=4 → floors at Tier 2)' },
  { key: 'e4AutonomyEnvelope',     label: 'E4 · Autonomy envelope',     hint: 'How much runs without a human in the loop?' },
  { key: 'e5BlastRadius',          label: 'E5 · Blast radius',          hint: 'Worst credible single-session harm / reversibility' },
];

const ANCHOR_LABELS = ['0 · none', '1 · low', '2 · moderate', '3 · high', '4 · critical'];

const prettyPurpose = (name) => (name || '')
  .toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' / ');

const tierTone = (tier) => ({
  TIER_1: 'danger', TIER_2: 'warning', TIER_3: 'action', TIER_4: 'muted',
}[tier] || 'muted');

const tierLabel = (tier) => (tier ? tier.replace('TIER_', 'Tier ') : '—');

const DEFAULT_INPUTS = {
  e1DecisionSignificance: 0,
  e2ActionReach: 0,
  e3DataSensitivity: 0,
  e4AutonomyEnvelope: 0,
  e5BlastRadius: 0,
  purposeClass: 'INTERNAL_ENABLEMENT',
  hasProductionEntitlement: false,
};

export default function MaterialityAssessSlideout({ agent, onClose, onScored }) {
  const open = !!agent;
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [purposeClasses, setPurposeClasses] = useState([]);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  // On open, pre-fill from the inputs currently on record (so a re-assessment EDITS the last
  // examiner-supplied values instead of starting blank); fall back to neutral defaults for a
  // first assessment. The tier is always re-derived by the rubric on submit — never hand-set.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setInputs(DEFAULT_INPUTS); setPreview(null); setError(null); setEditing(false);
    (async () => {
      try {
        const res = await api.getMaterialityInputs(agent.agentId);
        const cur = res && res.data !== undefined ? res.data : res;
        if (alive && cur && typeof cur === 'object'
            && ('e1DecisionSignificance' in cur || 'purposeClass' in cur)) {
          setInputs({ ...DEFAULT_INPUTS, ...cur });
          setEditing(true);
        }
      } catch (_e) { /* first assessment — keep neutral defaults */ }
    })();
    return () => { alive = false; };
  }, [open, agent?.agentId]);

  // Load the purpose-class list (backend-driven so it never drifts from the enum).
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      try {
        const info = unwrap(await api.getMaterialityRubric());
        if (alive && Array.isArray(info?.purposeClasses)) setPurposeClasses(info.purposeClasses);
      } catch (_e) { /* form still works with a static fallback below */ }
    })();
    return () => { alive = false; };
  }, [open]);

  const classes = purposeClasses.length ? purposeClasses : [
    { name: 'RISK_REGULATORY', multiplier: 1.4 },
    { name: 'REVENUE_CUSTOMER', multiplier: 1.2 },
    { name: 'INTERNAL_ENABLEMENT', multiplier: 1.0 },
    { name: 'EXPERIMENTAL_SANDBOX', multiplier: 0.6 },
  ];

  // Live rubric preview (pure endpoint — no persistence) whenever inputs change.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = unwrap(await api.previewMateriality(inputs));
        if (alive) setPreview(res);
      } catch (_e) { if (alive) setPreview(null); }
    })();
    return () => { alive = false; };
  }, [open, inputs]);

  const setFactor = useCallback((key, value) => {
    setInputs((p) => ({ ...p, [key]: Number(value) }));
  }, []);

  const submit = useCallback(async () => {
    if (!agent?.agentId) return;
    setSubmitting(true); setError(null);
    try {
      const res = await api.assessMateriality(agent.agentId, inputs);
      if (res && res.ok === false) throw new Error(res.error || 'Could not assess materiality.');
      onScored?.(unwrap(res));
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Could not assess materiality.');
    } finally {
      setSubmitting(false);
    }
  }, [agent, inputs, onScored, onClose]);

  const header = (
    <div>
      <div className="text-[14px] font-semibold text-v2-ink flex items-center gap-2">
        <Gauge size={15} /> {editing ? 'Re-assess materiality' : 'Assess materiality'}
      </div>
      <div className="text-[12px] text-v2-ink-muted truncate">{agent?.agentName || 'Agent'}</div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[12px] text-v2-ink-muted">
        {preview
          ? <>Rubric tier <b className="text-v2-ink">{tierLabel(preview.tier)}</b>{preview.boundaryCase ? ' · boundary case' : ''}</>
          : 'Enter charter facts to compute the tier'}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" loading={submitting} iconLeft={<ShieldCheck size={14} />} onClick={submit}>
          Assess &amp; record
        </Button>
      </div>
    </div>
  );

  return (
    <Slideout isOpen={open} onClose={onClose} ariaLabel="Assess materiality" header={header} footer={footer} width={520}>
      <div className="p-4 space-y-4">
        <p className="text-[12.5px] text-v2-ink-secondary">
          Materiality is computed by the platform rubric (v1.0) from these explicit charter facts —
          the tier is mapped, never hand-set. Supply what you can defend to an examiner.
        </p>

        {FACTORS.map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            <Select value={inputs[f.key]} onChange={(e) => setFactor(f.key, e.target.value)}>
              {ANCHOR_LABELS.map((lbl, i) => <option key={i} value={i}>{lbl}</option>)}
            </Select>
            <div className="text-[11px] text-v2-ink-muted mt-1">{f.hint}</div>
          </div>
        ))}

        <div>
          <Label>Purpose class</Label>
          <Select value={inputs.purposeClass} onChange={(e) => setInputs((p) => ({ ...p, purposeClass: e.target.value }))}>
            {classes.map((c) => (
              <option key={c.name} value={c.name}>{prettyPurpose(c.name)} (×{c.multiplier})</option>
            ))}
          </Select>
          <div className="text-[11px] text-v2-ink-muted mt-1">Highest applicable function — multiplies exposure.</div>
        </div>

        <label className="flex items-center gap-2 text-[12.5px] text-v2-ink-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={inputs.hasProductionEntitlement}
            onChange={(e) => setInputs((p) => ({ ...p, hasProductionEntitlement: e.target.checked }))}
          />
          Holds a production entitlement (Tier 4 is reserved for entitlement-free sandboxes)
        </label>

        {/* Live rubric preview */}
        <div className="rounded-v2-md border border-v2-border-subtle bg-v2-muted p-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-v2-ink-secondary mb-2">Rubric preview</div>
          {preview ? (
            <div className="flex flex-wrap items-center gap-3 text-[12px] text-v2-ink-secondary">
              <Pill tone={tierTone(preview.tier)}>{tierLabel(preview.tier)}</Pill>
              <span>M = <b className="text-v2-ink tabular-nums">{Number(preview.materiality).toFixed(1)}</b></span>
              <span>E {preview.exposure} · P ×{preview.purposeMultiplier}</span>
              {preview.boundaryCase ? <Pill tone="warning">boundary</Pill> : null}
              {(preview.appliedOverrides || []).length > 0 && (
                <span className="text-[11px] text-v2-ink-muted">overrides: {preview.appliedOverrides.join(', ')}</span>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-v2-ink-muted">Adjust the factors to see the computed tier.</div>
          )}
        </div>

        {error && <div className="text-[12px] text-v2-danger">{error}</div>}
      </div>
    </Slideout>
  );
}
